import { FC, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { makeStyles } from '@material-ui/core/styles';
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  Tooltip
} from '@mui/material';

import {
  CloseButton,
  FaIcon,
  If,
  MDBox,
  MDButton,
  MDTypography
} from '@components';
import ChartSvgUpload from '../ChartSvgUpload';

import { RootState } from '@store/store';
import { setPlanUpdateErrors } from '@store/AppSlice';

import {
  useGenerateCleanedDataFileMutation,
  useGenerateProjectReportMutation
} from '@api/projects';
import { useLazyGetFeatureAccessQuery } from '@api/usePlans';
import { setUserNotifications } from '@components/Notification/slice';

import { sendAmplitudeData } from '@utils/amplitude';
import { sendGAData } from '@utils/googleAnalytics';
import { getPlanName } from '@utils/common';
import { ChartConfigType } from '@DStypes/chart';
import { MSG_EVENT, MSG_TYPE, NotificationData } from '@DStypes/notifications';
import { FeatureTypes } from '@constants/common';
import { PricingPlans } from '@constants/pricing';
import {
  CLICKED_DOWNLOAD_CLEANED_FILE,
  CLICKED_DOWNLOAD_REPORT,
  CLICKED_REMOVE_WATERMARK,
  DOWNLOADED_CLEANED_FILE,
  REPORT_DOWNLOADED
} from '@constants/eventNames';

interface customiseOptionsProps {
  name: string;
  id:
    | 'project_name'
    | 'project_short_desc'
    | 'project_long_desc'
    | 'chart_name'
    | 'chart_short_desc'
    | 'chart_long_desc'
    | 'branding';
  disabled?: boolean;
}

interface Customisation {
  visible: boolean;
  alignment: string;
}

interface ReportCustomisation {
  project_name: Customisation;
  project_short_desc: Customisation;
  project_long_desc: Customisation;
  chart_name: Customisation;
  chart_short_desc: Customisation;
  chart_long_desc: Customisation;
  branding: boolean;
}

const buttonStyles = {
  textTransform: 'none',
  borderRadius: '6px !important',
  width: { xs: '50%', sm: 'auto' },
  px: '3rem',
  fontSize: '14px'
};

interface ReportGenerationModalProps {
  open: boolean;
  onClose: (charts?: string[]) => void;
  themeData: string[];
  chartConfigs: ChartConfigType[];
  svgGeneratedCharts: string[];
  selectedChartIds?: string[];
  disableDownloadReport: boolean;
  cleanedColumnsRedirect: boolean;
}

const useStyles = makeStyles(() => ({
  radioIcon: {
    '&.MuiRadio-root': {
      '& .MuiSvgIcon-root': {
        borderColor: '#344767'
      },
      '&.Mui-checked': {
        '& .MuiSvgIcon-root': {
          borderColor: '#DD6B20'
        }
      }
    }
  }
}));

const ReportGenerationModal: FC<ReportGenerationModalProps> = ({
  open,
  onClose,
  themeData,
  chartConfigs,
  cleanedColumnsRedirect,
  svgGeneratedCharts,
  disableDownloadReport,
  selectedChartIds = []
}) => {
  const classes = useStyles();
  const reduxDispatch = useDispatch();

  const projectBoard = useSelector((state: RootState) => state?.projectBoard);
  const projectData = projectBoard?.currentProjectData;

  const userDetails = useSelector((state: RootState) => state?.userDetails);
  const subscriptionFlag = useSelector(
    (state: RootState) => state?.AppData?.subKeyFlag
  );
  const isDownloadCleanedActionsEnabledByPlan = useSelector(
    (state: RootState) =>
      state?.AppData?.currentPlanFeatures?.downloadCleanedFile
  );

  const isDownloadReportEnabledByPlan = useSelector(
    (state: RootState) => state?.AppData?.currentPlanFeatures?.downloadReport
  );

  const isRemoveWaterMarkEnabledByPlan = useSelector(
    (state: RootState) => state?.AppData?.currentPlanFeatures?.removeWaterMark
  );
  const partialUploadColumnLimit = useSelector((state: RootState) => {
    if (
      projectData?.plan_at_creation?.plan_code ===
      PricingPlans.NO_ACTIVE_SUBSCRIPTION
    )
      return state?.AppData?.freePlanFeatures?.COLUMNS_LIMIT;
    else return state?.AppData?.litePlanFeatures?.COLUMNS_LIMIT;
  });

  const [generateReport] = useGenerateProjectReportMutation();
  const [generateCleanedDataFile] = useGenerateCleanedDataFileMutation();
  const [getFeatureAccess] = useLazyGetFeatureAccessQuery();

  const [activePage, setActivePage] = useState<number>(
    cleanedColumnsRedirect ? 2 : 1
  );
  const [appliedFormat, setAppliedFormat] = useState<string>('PPT');
  const [downloadOption, setDownloadOption] = useState<string>(
    cleanedColumnsRedirect ? 'download_cleaned_file' : ''
  );
  const [cleanedFileDownloadOption, setCleanedFileDownloadOption] =
    useState<string>('download_original_upload');
  const [includeAddOnColumns, setIncludeAddOnColumns] = useState<boolean>(
    projectData?.project_has_addon_fields || false
  );
  const [includeAutoCatColumns, setIncludeAutoCatColumns] = useState<boolean>(
    projectData?.project_has_auto_categorized_fields || false
  );
  const [error, setError] = useState<{ type: string; message: string }>();
  const [reportCustomisationObj, setReportCustomisationObj] =
    useState<ReportCustomisation>({
      project_name: { visible: true, alignment: 'left' },
      project_short_desc: {
        visible: projectData?.project_short_desc.length !== 0,
        alignment: 'left'
      },
      project_long_desc: {
        visible:
          projectData?.project_long_desc.length !== 0 &&
          !projectData?.project_long_desc.startsWith('Junk Data:'),
        alignment: 'left'
      },
      chart_name: { visible: true, alignment: 'left' },
      chart_short_desc: {
        visible: true,
        alignment: 'left'
      },
      chart_long_desc: {
        visible: true,
        alignment: 'left'
      },
      branding: true
    });

  const formats = [
    {
      label: 'PPT',
      value: 'PPT'
    },
    {
      label: 'PDF',
      value: 'PDF'
    }
  ];

  const customiseOptions: customiseOptionsProps[] = [
    { name: 'Project Name', id: 'project_name' },
    {
      name: 'Project Short Description',
      id: 'project_short_desc',
      disabled: projectData?.project_short_desc.length === 0
    },
    {
      name: 'Project Long Description',
      id: 'project_long_desc',
      disabled:
        projectData?.project_long_desc.length === 0 ||
        projectData?.project_long_desc.startsWith('Junk Data:')
    },
    { name: 'Chart Name', id: 'chart_name' },
    { name: 'Chart Short Description', id: 'chart_short_desc' },
    { name: 'Chart Long Description', id: 'chart_long_desc' }
  ];

  if (subscriptionFlag)
    customiseOptions.push({
      name: 'Branding (Powered by DataSquirrel)',
      id: 'branding'
    });

  const totalColumns =
    projectBoard?.currentProjectData?.project_total_columns || 0;

  const downloadOptions = [
    {
      label: 'Download cleaned file',
      value: 'download_cleaned_file'
    },
    {
      label: 'Download PPT/PDF report',
      value: 'download_report'
    }
  ];

  const cleanedFileDownloadOptions = [
    {
      label: `Download only the imported columns`,
      columnCount: `(${
        projectBoard?.currentProjectData?.project_imported_columns
      } out of total ${
        projectBoard?.currentProjectData?.project_remark
          ? totalColumns > partialUploadColumnLimit
            ? partialUploadColumnLimit
            : totalColumns
          : totalColumns
      }).`,
      sublabel:
        '[Recommended if you want to use, view or share only the columns uploaded and cleaned in previous step]',
      tooltipTitle:
        'If any column names were changed by you in the previous step or by DataSquirrel (Ex: Conversion changed to Conversion %), the same will be shown in the downloaded file.',
      value: 'download_cleaned_columns'
    },
    {
      label: `Download all columns as per original upload`,
      columnCount: `(${
        projectBoard?.currentProjectData?.project_remark
          ? totalColumns > partialUploadColumnLimit
            ? partialUploadColumnLimit
            : totalColumns
          : totalColumns
      } out of total ${
        projectBoard?.currentProjectData?.project_remark
          ? totalColumns > partialUploadColumnLimit
            ? partialUploadColumnLimit
            : totalColumns
          : totalColumns
      }).`,
      sublabel:
        '[Recommended if you want to upload data in original structure in a software]',
      tooltipTitle:
        'If any column names were changed by you in the previous step or by DataSquirrel (Ex: Conversion changed to Conversion %), they will NOT be updated in the downloaded file.',
      value: 'download_original_upload'
    }
  ];

  const unSavedCharts = useMemo(
    () =>
      chartConfigs.filter(
        (chartConfig: ChartConfigType) =>
          !svgGeneratedCharts.includes(chartConfig.id) &&
          (selectedChartIds.length
            ? selectedChartIds.includes(chartConfig.id)
            : true)
      ),
    []
  );

  const onGenerateReport = () => {
    generateReport({
      id: projectBoard?.currentProjectData?.id,
      payload: {
        report_params: {
          ...reportCustomisationObj,
          branding: reportCustomisationObj.branding
        },
        report_type: appliedFormat,
        ...(selectedChartIds.length && { chart_ids: selectedChartIds })
      }
    });
  };

  const handleNext = () => {
    if (
      (activePage === 1 && !downloadOption) ||
      (activePage === 2 &&
        downloadOption === 'download_cleaned_file' &&
        !cleanedFileDownloadOption)
    ) {
      setError({ type: 'download_option', message: 'Please select an option' });

      return;
    }
    if (
      activePage === 2 &&
      downloadOption === 'download_report' &&
      !appliedFormat
    ) {
      setError({ type: 'format', message: 'Please select a format' });

      return;
    }

    if (activePage === 1 && downloadOption === 'download_cleaned_file')
      sendAmplitudeData(CLICKED_DOWNLOAD_CLEANED_FILE);

    if (
      activePage === 2 &&
      downloadOption === 'download_report' &&
      appliedFormat
    )
      sendAmplitudeData(CLICKED_DOWNLOAD_REPORT, {
        'Total Charts / Downloaded charts': `${chartConfigs.length}/${
          selectedChartIds.length || chartConfigs.length
        }`,
        'Report Type': appliedFormat
      });

    if (activePage === 3) {
      sendAmplitudeData(REPORT_DOWNLOADED);
      sendGAData(REPORT_DOWNLOADED);
    }

    if (!cleanedColumnsRedirect)
      setActivePage((prevActivePage) => prevActivePage + 1);

    if (activePage === 2 && downloadOption === 'download_cleaned_file') {
      generateCleanedDataFile({
        id: projectBoard?.currentProjectData?.id,
        payload: {
          file_clean_params: {
            download_all_columns:
              cleanedFileDownloadOption === 'download_original_upload'
                ? true
                : false,
            include_ds_addon: includeAddOnColumns,
            include_auto_categorized: includeAutoCatColumns
          }
        }
      });
      sendAmplitudeData(DOWNLOADED_CLEANED_FILE);
      onClose();
      onReportGenerationStart('File preparation');
    }
  };

  const handleBack = () => {
    if (activePage === 1 || cleanedColumnsRedirect) onClose();
    if (activePage !== 1) setActivePage((prevActivePage) => prevActivePage - 1);
    setError({ type: '', message: '' });
  };

  const onApplyFormat = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === 'download_cleaned_file') {
      if (subscriptionFlag) {
        // If feature not accessible by plan then return
        const response = await getFeatureAccess(
          FeatureTypes.DOWNLOAD_CLEANED_FILE
        );

        if (!response?.isSuccess) return;
      }
    } else if (event.target.value === 'download_report') {
      if (subscriptionFlag) {
        // If feature not accessible by plan then return
        const response = await getFeatureAccess(FeatureTypes.DOWNLOAD_REPORTS);

        if (!response?.isSuccess) return;
      }
    }

    if (activePage === 1) setDownloadOption(event.target.value);
    else if (activePage === 2)
      if (downloadOption === 'download_report')
        setAppliedFormat(event.target.value);
      else setCleanedFileDownloadOption(event.target.value);
    setError({ type: '', message: '' });
  };

  const onChangeVisible = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: customiseOptionsProps['id']
  ) => {
    if (id !== 'project_name' && id !== 'chart_name' && id !== 'branding') {
      setReportCustomisationObj((prevState) => ({
        ...prevState,
        [id]: { ...prevState?.[id], visible: e.target.checked }
      }));
    } else if (id === 'branding') {
      if (!e.target.checked)
        sendAmplitudeData(CLICKED_REMOVE_WATERMARK, {
          location_type: 'Report'
        });
      if (!isRemoveWaterMarkEnabledByPlan && subscriptionFlag)
        reduxDispatch(
          setPlanUpdateErrors({
            button_text: 'Choose a plan',
            feature_name: 'Remove watermark',
            modal_name: 'Feature Limit',
            title: `Access this feature with an upgrade to your current ${getPlanName(
              userDetails.plan,
              userDetails.is_trial
            )} plan.`,
            sub_title:
              'Upgrade your plan to enjoy using advanced features. Simply click below and choose from a wide range of plans that suit your needs.'
          })
        );
      else
        setReportCustomisationObj((prevState) => ({
          ...prevState,
          branding: !prevState?.branding
        }));
    }
  };

  const onChangeAlignment = (
    id: customiseOptionsProps['id'],
    alignment: 'left' | 'center'
  ) => {
    if (id !== 'branding')
      setReportCustomisationObj((prevState) => ({
        ...prevState,
        [id]: { ...prevState?.[id], alignment: alignment }
      }));
  };

  const onChangeAddOnColumnsToggle = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIncludeAddOnColumns(event.target.checked);
  };

  const onChangeAutoCatColumnsTogle = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIncludeAutoCatColumns(event.target.checked);
  };

  const getTooltipTitle = (option: customiseOptionsProps) => {
    if (option.id === 'project_name' || option.id === 'chart_name')
      return `This cannot be unselected`;

    if (option.disabled)
      return `There is no ${option.name} available. You can go back and select edit icon next to Project Name to provide one.`;

    return ``;
  };

  const onReportGenerationStart = (customMessageText: string) => {
    const payload: NotificationData = {
      created_at: dayjs(new Date())
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      id: dayjs(new Date()).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      is_read: false,
      is_silent: false,
      is_user_message: true,
      message_event: MSG_EVENT.REPORT_GENERATION_INITIATED,
      message_text: `${customMessageText} has started for the ${
        projectBoard?.currentProjectData?.project_name
      } project. Please wait for the next message to ${
        customMessageText === 'File preparation'
          ? 'see the cleaned file.'
          : 'see the report.'
      }`,
      updated_at: dayjs(new Date())
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      message_type: MSG_TYPE.INFO,
      message_data: JSON.stringify({
        project: {
          id: projectBoard?.currentProjectData?.id,
          name: projectBoard?.currentProjectData?.project_name
        }
      }),
      is_important: false,
      recipient_obj: 0
    };

    reduxDispatch(setUserNotifications(payload));
  };

  const getDownloadOptions = (value: string) => {
    if (value === 'download_cleaned_file')
      return (
        <FaIcon
          icon={isDownloadCleanedActionsEnabledByPlan ? 'lock-open' : 'lock'}
          style={{ marginRight: '10px', marginBottom: '1px' }}
          typographyProps={{
            variant: 'lg',
            color: 'primary'
          }}
        />
      );
    else if (value === 'download_report')
      return (
        <FaIcon
          icon={isDownloadReportEnabledByPlan ? 'lock-open' : 'lock'}
          style={{ marginRight: '10px', marginBottom: '1px' }}
          typographyProps={{
            variant: 'lg',
            color: 'primary'
          }}
        />
      );
  };

  return (
    <Dialog
      open={open}
      maxWidth='xs'
      fullWidth
      onClose={() => onClose()}
      sx={{
        zIndex: 100,
        '& .MuiPaper-root': {
          position: 'relative',
          overflow: 'visible'
        }
      }}
    >
      <CloseButton onClose={() => onClose()} />
      <DialogTitle
        sx={{ px: 4, height: 'auto', pb: 0 }}
        mb={{ xs: 2, sm: 0 }}
        {...({ component: 'div' } as any)}
      >
        <If condition={activePage === 1}>
          <MDTypography variant='h3' color='secondary' textGradient mb='5px'>
            Select a Download Option
          </MDTypography>
        </If>
        <If condition={activePage === 2}>
          <If
            condition={
              downloadOption === 'download_cleaned_file' ||
              cleanedColumnsRedirect
            }
          >
            <MDTypography variant='h3' color='secondary' textGradient mb='5px'>
              Select an option
            </MDTypography>
          </If>
          <If condition={downloadOption === 'download_report'}>
            <MDTypography variant='h3' color='secondary' textGradient mb='5px'>
              Select Download Format
            </MDTypography>
          </If>
        </If>
        <If condition={activePage === 3}>
          <MDTypography variant='h3' color='secondary' textGradient mb='5px'>
            Customise {appliedFormat} Report
          </MDTypography>
        </If>
        <If condition={activePage === 4}>
          <MDBox>
            <MDTypography variant='h3' color='secondary' textGradient mb='5px'>
              Download Report
            </MDTypography>
          </MDBox>
        </If>
      </DialogTitle>
      <DialogContent sx={{ p: 4, height: '500px' }}>
        <If condition={activePage === 1}>
          <MDBox display='flex' flexDirection='column'>
            <RadioGroup onChange={onApplyFormat}>
              {downloadOptions.map((option, index) => (
                <FormControlLabel
                  control={
                    <Radio
                      color='secondary'
                      size='medium'
                      sx={{
                        mb: 0.3
                      }}
                      className={classes.radioIcon}
                    />
                  }
                  value={option?.value}
                  label={
                    <MDTypography
                      variant='h5'
                      opacity={disableDownloadReport && index === 1 ? 0.5 : 1}
                      lineHeight='1.25rem'
                    >
                      {subscriptionFlag && getDownloadOptions(option.value)}
                      {option.label}
                    </MDTypography>
                  }
                  key={option.label}
                  checked={option.value === downloadOption}
                  disabled={disableDownloadReport && index === 1}
                />
              ))}
            </RadioGroup>
            {error?.type === 'download_option' && (
              <MDTypography variant='caption' sx={{ color: 'red' }}>
                {error.message}
              </MDTypography>
            )}
          </MDBox>
        </If>
        <If condition={activePage === 2}>
          <If condition={downloadOption === 'download_report'}>
            <MDBox display='flex' flexDirection='column'>
              <MDTypography variant='h5' color='info' sx={{ mb: 2 }}>
                {`You are about to download your report with ${
                  selectedChartIds.length || chartConfigs.length
                } out of ${chartConfigs.length} number of
              charts in the below selected Report format:`}
              </MDTypography>
              <RadioGroup onChange={onApplyFormat}>
                {formats.map((format) => (
                  <FormControlLabel
                    control={
                      <Radio
                        color='secondary'
                        size='medium'
                        sx={{ mb: 0.3 }}
                        className={classes.radioIcon}
                      />
                    }
                    value={format.value}
                    label={
                      <MDTypography variant='h5'>{format.label}</MDTypography>
                    }
                    key={format.label}
                    checked={format.value === appliedFormat}
                  />
                ))}
              </RadioGroup>
              {error?.type === 'format' && (
                <MDTypography variant='caption' sx={{ color: 'red' }}>
                  {error.message}
                </MDTypography>
              )}
            </MDBox>
          </If>
          <If
            condition={
              downloadOption === 'download_cleaned_file' ||
              cleanedColumnsRedirect
            }
          >
            <MDBox>
              <MDBox display='flex' flexDirection='column'>
                <RadioGroup onChange={onApplyFormat}>
                  {cleanedFileDownloadOptions.map((option) => (
                    <FormControlLabel
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginBottom: activePage === 2 ? '20px' : '0px'
                      }}
                      control={
                        <Radio
                          color='secondary'
                          size='medium'
                          sx={{ mb: 0.3 }}
                          className={classes.radioIcon}
                        />
                      }
                      value={option?.value}
                      label={
                        <MDBox display={'flex'}>
                          <MDBox display={'flex'} flexDirection={'column'}>
                            <MDTypography
                              variant='h5'
                              marginTop={activePage === 2 ? '3px' : '0px'}
                              lineHeight='1.25rem'
                            >
                              {option.label}
                            </MDTypography>
                            <MDTypography variant='h5' lineHeight='1.25rem'>
                              {option.columnCount}
                            </MDTypography>
                            <MDTypography
                              sx={{ color: '#808080' }}
                              fontSize='12px'
                              marginTop={activePage === 2 ? '3px' : '0px'}
                            >
                              {option.sublabel}
                            </MDTypography>
                          </MDBox>
                          <Tooltip
                            title={option.tooltipTitle}
                            placement='bottom-start'
                            PopperProps={{
                              style: {
                                zIndex: 9999
                              }
                            }}
                          >
                            <MDBox
                              height={'fit-content'}
                              onClick={(event) => {
                                event.preventDefault();
                              }}
                            >
                              <FaIcon
                                icon='circle-question'
                                style={{
                                  verticalAlign: 'middle'
                                }}
                                typographyProps={{
                                  color: 'info'
                                }}
                              />
                            </MDBox>
                          </Tooltip>
                        </MDBox>
                      }
                      key={option.label}
                      checked={option.value === cleanedFileDownloadOption}
                    />
                  ))}
                </RadioGroup>
                <MDBox display={'flex'} marginTop={'30px'}>
                  <Tooltip
                    title={
                      !projectData?.project_has_addon_fields
                        ? 'No add-on columns applicable to this data'
                        : ''
                    }
                    placement='top'
                  >
                    <div>
                      <Switch
                        checked={includeAddOnColumns}
                        disabled={!projectData?.project_has_addon_fields}
                        onChange={onChangeAddOnColumnsToggle}
                      />
                    </div>
                  </Tooltip>
                  <MDTypography
                    variant='h5'
                    position='relative'
                    bottom='6px'
                    marginLeft='13px'
                    color='info'
                    fontSize='15px'
                    opacity={projectData?.project_has_addon_fields ? 1 : 0.5}
                  >
                    Include add-on columns suggested by DataSquirrel
                    <br />
                    {'\u0028'}Ex: Column with standardization currency{'\u0029'}
                  </MDTypography>
                </MDBox>
                <MDBox display={'flex'} marginTop={2}>
                  <Tooltip
                    title={
                      !projectData?.project_has_auto_categorized_fields
                        ? 'No auto-categorized columns for this data'
                        : ''
                    }
                    placement='top'
                  >
                    <span>
                      <Switch
                        checked={includeAutoCatColumns}
                        disabled={
                          !projectData?.project_has_auto_categorized_fields
                        }
                        onChange={onChangeAutoCatColumnsTogle}
                      />
                    </span>
                  </Tooltip>
                  <MDTypography
                    variant='h5'
                    position='relative'
                    bottom='6px'
                    marginLeft='13px'
                    color='info'
                    fontSize='15px'
                    opacity={
                      projectData?.project_has_auto_categorized_fields ? 1 : 0.5
                    }
                  >
                    Include auto-categorised columns by you
                    <br /> (Ex: Column categorized in Chart page using DS
                    Recommendations)
                  </MDTypography>
                </MDBox>
              </MDBox>
              {error?.type === 'download_option' && (
                <MDTypography variant='caption' sx={{ color: 'red' }}>
                  {error.message}
                </MDTypography>
              )}
            </MDBox>
          </If>
        </If>
        <If condition={activePage === 3}>
          <MDBox>
            <MDBox display='flex' flexDirection='column' rowGap={1.5}>
              {customiseOptions.map((option, index) => (
                <MDBox
                  display='flex'
                  alignItems={{ sm: 'center' }}
                  justifyContent='space-between'
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  key={index}
                >
                  <MDBox display='flex'>
                    <Tooltip
                      title={getTooltipTitle(option)}
                      PopperProps={{ style: { zIndex: 9999 } }}
                    >
                      <MDBox>
                        <Checkbox
                          checked={
                            option.id === 'branding'
                              ? reportCustomisationObj?.branding
                              : reportCustomisationObj?.[option.id]?.visible
                          }
                          disabled={option?.disabled}
                          sx={{ width: 24, height: 24 }}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onChangeVisible(e, option.id)
                          }
                        />
                      </MDBox>
                    </Tooltip>
                    <MDTypography
                      variant='h5'
                      color='info'
                      marginTop='1px'
                      opacity={option.disabled ? 0.6 : 1}
                    >
                      {option.name}
                    </MDTypography>
                  </MDBox>
                  {option.id !== 'branding' && (
                    <MDBox
                      display='flex'
                      columnGap={2}
                      justifyContent='center'
                      alignItems='center'
                      mt={1}
                      opacity={option.disabled ? 0.6 : 1}
                    >
                      <IconButton
                        sx={{ p: 0 }}
                        onClick={() => onChangeAlignment(option.id, 'left')}
                      >
                        <FaIcon
                          icon='align-left'
                          border
                          style={{
                            borderRadius: '6px',
                            border:
                              reportCustomisationObj?.[option.id]?.alignment ===
                                'left' && !option.disabled
                                ? '2px solid #DD6B20'
                                : '1px solid'
                          }}
                          size='lg'
                        />
                      </IconButton>
                      <IconButton
                        sx={{ p: 0 }}
                        onClick={() => onChangeAlignment(option.id, 'center')}
                      >
                        <FaIcon
                          icon='align-center'
                          border
                          style={{
                            borderRadius: '6px',
                            border:
                              reportCustomisationObj?.[option.id]?.alignment ===
                                'center' && !option.disabled
                                ? '2px solid #DD6B20'
                                : '1px solid'
                          }}
                          size='lg'
                        />
                      </IconButton>
                    </MDBox>
                  )}
                </MDBox>
              ))}
            </MDBox>
          </MDBox>
        </If>
        <If condition={activePage === 4}>
          <ChartSvgUpload
            chartConfigs={unSavedCharts}
            projectColorSet={themeData}
            projectId={projectBoard?.currentProjectData?.id as string}
            onComplete={async (charts?: any) => {
              await onGenerateReport();
              onReportGenerationStart('Report generation');
              onClose(charts);
            }}
          />
        </If>
      </DialogContent>
      <DialogActions
        sx={{ justifyContent: 'space-between', alignItems: 'end' }}
      >
        <MDTypography
          variant='caption'
          sx={{
            opacity: activePage === 4 || cleanedColumnsRedirect ? 0 : 1
          }}
        >
          {activePage}/
          {downloadOption === 'download_cleaned_file'
            ? 2
            : downloadOption === 'download_report'
            ? 3
            : 1}
        </MDTypography>

        {activePage !== 4 && (
          <MDBox
            display='flex'
            flexWrap='wrap'
            justifyContent='flex-end'
            columnGap={2}
            rowGap={1}
          >
            <MDButton
              color='secondary'
              variant='outlined'
              onClick={handleBack}
              sx={{ ...buttonStyles }}
            >
              {activePage === 1 || cleanedColumnsRedirect ? 'Cancel' : 'Back'}
            </MDButton>
            <MDButton
              color='primary'
              variant='gradient'
              onClick={handleNext}
              sx={{ ...buttonStyles }}
            >
              {activePage === 2
                ? downloadOption === 'download_cleaned_file' ||
                  cleanedColumnsRedirect
                  ? 'Download Cleaned File'
                  : 'Next'
                : activePage === 1
                ? 'Next'
                : 'Generate Report'}
            </MDButton>
          </MDBox>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReportGenerationModal;
